import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Heart, MessageCircle, MapPin, CalendarDays, ArrowUpRight } from "lucide-react";
import { API_URL, LOCAL_URL } from "../constants";
import {
  Avatar,
  DownloadAppCard,
  EventCard,
  PageError,
  PageLoading,
  PublicShell,
  postImagePlaceholder,
} from "../components/PublicPage";
import { useOpenInApp } from "../helpers/deep-link-wrapper";
import PageMetadata from "../components/PageMetadata";

export default function UserProfile() {
  const { id, username } = useParams();
  const [user, setUser] = useState<any>();
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    fetch(
      `${process.env.NODE_ENV === "production" ? API_URL : LOCAL_URL}/website/${username ? `user/username/${encodeURIComponent(username)}` : `user/${id}`}`,
    )
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then(setUser)
      .catch(() => setFailed(true));
  }, [id, username]);
  useOpenInApp(user?.username ? encodeURIComponent(user.username) : undefined);

  if (failed) return <PageError message="This profile isn't available." />;
  if (!user) return <PageLoading />;
  const name =
    `${user.firstName || ""} ${user.lastName || ""}`.trim() ||
    "CityMoshi member";
  const compactCount = (count = 0) => {
    if (count < 1_000) return String(count);
    if (count < 1_000_000) return `${(count / 1_000).toFixed(count >= 10_000 ? 0 : 1).replace(/\.0$/, "")}K`;
    return `${(count / 1_000_000).toFixed(1).replace(/\.0$/, "")}M`;
  };
  return (
    <PublicShell className="page-profile">
      <PageMetadata
        title={user.username ? `${name} (@${user.username})` : name}
        description={`Discover ${name}'s events and community moments on CityMoshi.`}
        image={user.picture_original || user.picture}
        url={user.username ? `/${user.username}` : `/users/${user.id}`}
      />
      <main className="wrap detail-main detail-profile">
        <section className="detail-profile-hero">
          <div className="detail-profile-identity"><Avatar src={user.picture_original || user.picture} name={name} large /><div><p className="eyebrow">CITYMOSHI MEMBER</p><h1>{name}</h1>{user.username && <p className="detail-profile-handle">@{user.username}</p>}{user.country && <p className="detail-profile-location"><MapPin /> {user.country}</p>}</div></div>
          <div className="detail-profile-stats">
            <span>
              <b>{user._count?.followerRelations || 0}</b>Followers
            </span>
            <span>
              <b>{user._count?.followingRelations || 0}</b>Following
            </span>
            <span>
              <b>{user._count?.posts || 0}</b>Posts
            </span>
          </div>
        </section>
        <a className="btn btn--primary detail-profile-follow" href="#download">Follow in the app <ArrowUpRight /></a>
        {user.posts?.length > 0 && (
          <section className="detail-profile-section">
            <div className="detail-profile-section-heading"><div><p>RECENT ACTIVITY</p><h2>Posts by {user.firstName || "this member"}</h2></div><span>{user.posts.length} posts</span></div>
            <div className="detail-profile-posts">
              {user.posts.map((post: any) => (
                <Link
                  to={`/p/${post.event.id.replace(/-/g, "").slice(0, 8)}${post.code}`}
                  className={`detail-profile-post ${!(post.images?.length || post.videos?.length) ? "detail-profile-post-text" : ""}`}
                  key={post.id}
                >
                  {post.images?.[0] && (
                    <img
                      src={post.images[0]}
                      alt=""
                      onError={(e) => {
                        e.currentTarget.src = postImagePlaceholder;
                      }}
                    />
                  )}
                  <div>
                    {!(post.images?.length || post.videos?.length) && (
                      <header className="detail-profile-post-author">
                        <Avatar src={user.picture_original || user.picture} name={name} />
                        <span>
                          <strong>{name}</strong>
                          {user.username && <small>@{user.username}</small>}
                        </span>
                      </header>
                    )}
                    <p>{post.content || "Shared a moment"}</p>
                    {post.images?.length || post.videos?.length ? (
                      <>
                        <small>{post.event?.name}</small>
                        <footer>
                          <span>
                            <Heart /> {post._count?.likes || 0}
                          </span>
                          <span>
                            <MessageCircle /> {post._count?.comments || 0}
                          </span>
                          <ArrowUpRight />
                        </footer>
                      </>
                    ) : (
                      <footer className="detail-profile-post-metrics">
                        <span>{compactCount(post._count?.likes)} likes</span>
                        <span>{compactCount(post._count?.comments)} replies</span>
                      </footer>
                    )}
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}
        {user.favoriteEvents?.length > 0 && (
          <section className="detail-profile-section">
            <div className="detail-profile-section-heading"><div><p>ON THE CALENDAR</p><h2>Events they’re looking forward to</h2></div><CalendarDays /></div>
            <div className="detail-event-grid">
              {user.favoriteEvents.map((event: any) => (
                <EventCard key={event.id} event={event} />
              ))}
            </div>
          </section>
        )}
        {!user.posts?.length && !user.favoriteEvents?.length && <section className="detail-profile-section detail-empty"><h2>More moments to come.</h2><p>Follow {user.firstName || "this member"} in the app to stay connected.</p></section>}
        <DownloadAppCard />
      </main>
    </PublicShell>
  );
}
